/**
 * Loan Review Page Component
 * Hope SACCO - Review and approve/reject loan applications
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';  // ← ADD THIS LINE

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const LoanReviewPage = () => {
  const navigate = useNavigate();
  const { loanId } = useParams();
  
  const [loan, setLoan] = useState(null);
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showDisbursementModal, setShowDisbursementModal] = useState(false);
  
  const [approvalRole, setApprovalRole] = useState('treasurer'); // treasurer/chairman/secretary
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Disbursement fields
  const [disbursementMethod, setDisbursementMethod] = useState('cash');
  const [referenceNumber, setReferenceNumber] = useState('');

  useEffect(() => {
    fetchLoanDetails();
  }, [loanId]);

  const fetchLoanDetails = async () => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('hopeSaccoToken');
      
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/loans/${loanId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.clear();
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch loan details');
      }

      const data = await response.json();
      setLoan(data.data.loan);
      setMember(data.data.loan.memberDetails);

    } catch (error) {
      console.error('Fetch loan error:', error);
      setError('Failed to load loan details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle approval
  const handleApprove = async () => {
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('hopeSaccoToken');

      const response = await fetch(`${API_URL}/loans/${loanId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role: approvalRole,
          decision: 'approved',
          comments
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Loan approved successfully!');
        setShowApprovalModal(false);
        setComments('');
        fetchLoanDetails(); // Refresh loan data
      } else {
        setError(data.message || 'Failed to approve loan');
      }
    } catch (error) {
      console.error('Approval error:', error);
      setError('Failed to approve loan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle rejection
  const handleReject = async () => {
    if (!comments.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('hopeSaccoToken');

      const response = await fetch(`${API_URL}/loans/${loanId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role: approvalRole,
          decision: 'rejected',
          comments
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Loan rejected successfully!');
        setShowRejectionModal(false);
        setComments('');
        fetchLoanDetails();
      } else {
        setError(data.message || 'Failed to reject loan');
      }
    } catch (error) {
      console.error('Rejection error:', error);
      setError('Failed to reject loan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle disbursement
  const handleDisburse = async () => {
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('hopeSaccoToken');

      const response = await fetch(`${API_URL}/loans/${loanId}/disburse`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          disbursementMethod,
          referenceNumber: disbursementMethod === 'transfer' ? referenceNumber : undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Loan disbursed successfully!');
        setShowDisbursementModal(false);
        setReferenceNumber('');
        fetchLoanDetails();
      } else {
        setError(data.message || 'Failed to disburse loan');
      }
    } catch (error) {
      console.error('Disbursement error:', error);
      setError('Failed to disburse loan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    let date;
    if (dateValue.toDate) {
      date = dateValue.toDate();
    } else if (dateValue._seconds) {
      date = new Date(dateValue._seconds * 1000);
    } else {
      date = new Date(dateValue);
    }
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get approval status icon
  const getApprovalIcon = (status) => {
    if (status === 'approved') return '✓';
    if (status === 'rejected') return '✗';
    return '⏳';
  };

  // Get approval status class
  const getApprovalClass = (status) => {
    if (status === 'approved') return 'status-approved';
    if (status === 'rejected') return 'status-rejected';
    return 'status-pending';
  };

  // Check if loan can be disbursed
  const canDisburse = loan && 
    loan.status === 'approved' &&
    loan.treasurerApproval === 'approved' &&
    loan.chairmanApproval === 'approved' &&
    loan.secretaryApproval === 'approved';

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Loading loan details...</p>
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="error-page">
        <h2>Loan Not Found</h2>
        <p>The requested loan application could not be found.</p>
        <button onClick={() => navigate('/admin/loans/applications')}>
          Back to Applications
        </button>
      </div>
    );
  }

  return (
    <AdminLayout 
      activePage="loans" 
      pageTitle="Loan Application Review"
      breadcrumbs={[
        { label: 'Dashboard', link: '/admin/dashboard' },
        { label: 'Loan Applications', link: '/admin/loans/applications' },
        { label: 'Review' }
      ]}
    >

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="review-container">
        {/* Member Information Panel */}
        <div className="info-card">
          <h2>Member Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Member ID:</span>
              <span className="value">{member?.memberId}</span>
            </div>
            <div className="info-item">
              <span className="label">Name:</span>
              <span className="value">{loan.memberName}</span>
            </div>
            <div className="info-item">
              <span className="label">Phone:</span>
              <span className="value">{member?.personalInfo?.phoneNumber || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Email:</span>
              <span className="value">{member?.personalInfo?.email || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Total Shares:</span>
              <span className="value">{formatCurrency(member?.currentBalances?.totalShares || 0)}</span>
            </div>
            <div className="info-item">
              <span className="label">Available Shares:</span>
              <span className="value">{formatCurrency(member?.currentBalances?.availableShares || 0)}</span>
            </div>
            <div className="info-item">
              <span className="label">Existing Loans:</span>
              <span className="value">
                {member?.currentBalances?.activeLoans || 0} active
              </span>
            </div>
            <div className="info-item">
              <span className="label">Total Outstanding:</span>
              <span className="value">{formatCurrency(member?.currentBalances?.totalLoansOutstanding || 0)}</span>
            </div>
          </div>
        </div>

        {/* Loan Request Details */}
        <div className="info-card">
          <h2>Loan Request Details</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Loan ID:</span>
              <span className="value font-mono">{loan.loanId}</span>
            </div>
            <div className="info-item">
              <span className="label">Loan Type:</span>
              <span className="value">
                {loan.loanType === 'normal' ? 'Normal Loan' : 
                 loan.loanType === 'school' ? 'School Fees Loan' : 'Emergency Loan'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Amount Requested:</span>
              <span className="value highlight">{formatCurrency(loan.originalAmount)}</span>
            </div>
            <div className="info-item">
              <span className="label">Duration:</span>
              <span className="value">{loan.loanDuration} months</span>
            </div>
            <div className="info-item">
              <span className="label">Interest Rate:</span>
              <span className="value">{loan.interestRate * 100}% per month</span>
            </div>
            <div className="info-item">
              <span className="label">Application Date:</span>
              <span className="value">{formatDate(loan.applicationDate)}</span>
            </div>
            <div className="info-item">
              <span className="label">Application Fee:</span>
              <span className="value">{formatCurrency(loan.applicationFee)}</span>
            </div>
            <div className="info-item">
              <span className="label">Status:</span>
              <span className={`badge badge-${loan.status}`}>
                {loan.status.toUpperCase()}
              </span>
            </div>
          </div>

          {loan.purpose && (
            <div className="purpose-section">
              <h3>Purpose/Reason:</h3>
              <p>{loan.purpose}</p>
            </div>
          )}

          <div className="calculation-section">
            <h3>Loan Calculation</h3>
            <div className="calc-grid">
              <div className="calc-item">
                <span className="calc-label">Monthly Principal:</span>
                <span className="calc-value">{formatCurrency(loan.monthlyPrincipal)}</span>
              </div>
              <div className="calc-item">
                <span className="calc-label">Monthly Interest:</span>
                <span className="calc-value">{formatCurrency(loan.monthlyInterest)}</span>
              </div>
              <div className="calc-item highlight">
                <span className="calc-label">Total Monthly Payment:</span>
                <span className="calc-value">{formatCurrency(loan.monthlyPrincipal + loan.monthlyInterest)}</span>
              </div>
              <div className="calc-item">
                <span className="calc-label">Total Interest:</span>
                <span className="calc-value">{formatCurrency(loan.totalInterest)}</span>
              </div>
              <div className="calc-item">
                <span className="calc-label">Total Repayable:</span>
                <span className="calc-value">{formatCurrency(loan.totalRepayable)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Approval Workflow */}
        <div className="info-card">
          <h2>Approval Workflow</h2>
          <div className="approval-workflow">
            <div className={`approval-step ${getApprovalClass(loan.treasurerApproval)}`}>
              <div className="step-icon">{getApprovalIcon(loan.treasurerApproval)}</div>
              <div className="step-details">
                <h3>Treasurer</h3>
                <p className="step-status">{loan.treasurerApproval || 'Pending'}</p>
                {loan.treasurerApprovalDate && (
                  <p className="step-date">{formatDate(loan.treasurerApprovalDate)}</p>
                )}
                {loan.treasurerComments && (
                  <p className="step-comments">"{loan.treasurerComments}"</p>
                )}
              </div>
            </div>

            <div className={`approval-step ${getApprovalClass(loan.chairmanApproval)}`}>
              <div className="step-icon">{getApprovalIcon(loan.chairmanApproval)}</div>
              <div className="step-details">
                <h3>Chairman</h3>
                <p className="step-status">{loan.chairmanApproval || 'Pending'}</p>
                {loan.chairmanApprovalDate && (
                  <p className="step-date">{formatDate(loan.chairmanApprovalDate)}</p>
                )}
                {loan.chairmanComments && (
                  <p className="step-comments">"{loan.chairmanComments}"</p>
                )}
              </div>
            </div>

            <div className={`approval-step ${getApprovalClass(loan.secretaryApproval)}`}>
              <div className="step-icon">{getApprovalIcon(loan.secretaryApproval)}</div>
              <div className="step-details">
                <h3>Secretary</h3>
                <p className="step-status">{loan.secretaryApproval || 'Pending'}</p>
                {loan.secretaryApprovalDate && (
                  <p className="step-date">{formatDate(loan.secretaryApprovalDate)}</p>
                )}
                {loan.secretaryComments && (
                  <p className="step-comments">"{loan.secretaryComments}"</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {loan.status === 'pending' && (
            <div className="action-buttons">
              <button 
                className="btn btn-success"
                onClick={() => setShowApprovalModal(true)}
              >
                ✓ Approve Loan
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => setShowRejectionModal(true)}
              >
                ✗ Reject Loan
              </button>
            </div>
          )}

          {canDisburse && (
            <div className="action-buttons">
              <button 
                className="btn btn-primary"
                onClick={() => setShowDisbursementModal(true)}
              >
                💸 Disburse Loan
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="modal-overlay" onClick={() => setShowApprovalModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Approve Loan Application</h2>
            
            <div className="form-group">
              <label>Your Role *</label>
              <select 
                value={approvalRole} 
                onChange={(e) => setApprovalRole(e.target.value)}
              >
                <option value="treasurer">Treasurer</option>
                <option value="chairman">Chairman</option>
                <option value="secretary">Secretary</option>
              </select>
            </div>

            <div className="form-group">
              <label>Comments (Optional)</label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any comments or notes..."
                rows="4"
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowApprovalModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success"
                onClick={handleApprove}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Approving...' : 'Confirm Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="modal-overlay" onClick={() => setShowRejectionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Reject Loan Application</h2>
            
            <div className="form-group">
              <label>Your Role *</label>
              <select 
                value={approvalRole} 
                onChange={(e) => setApprovalRole(e.target.value)}
              >
                <option value="treasurer">Treasurer</option>
                <option value="chairman">Chairman</option>
                <option value="secretary">Secretary</option>
              </select>
            </div>

            <div className="form-group">
              <label>Reason for Rejection *</label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Please provide a reason for rejecting this loan..."
                rows="4"
                required
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowRejectionModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleReject}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disbursement Modal */}
      {showDisbursementModal && (
        <div className="modal-overlay" onClick={() => setShowDisbursementModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Disburse Loan</h2>
            
            <div className="disburse-summary">
              <p><strong>Member:</strong> {loan.memberName}</p>
              <p><strong>Amount:</strong> {formatCurrency(loan.originalAmount)}</p>
            </div>

            <div className="form-group">
              <label>Disbursement Method *</label>
              <select 
                value={disbursementMethod} 
                onChange={(e) => setDisbursementMethod(e.target.value)}
              >
                <option value="cash">Cash</option>
                <option value="transfer">Bank Transfer</option>
              </select>
            </div>

            {disbursementMethod === 'transfer' && (
              <div className="form-group">
                <label>Reference Number *</label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="Enter transaction reference..."
                  required
                />
              </div>
            )}

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowDisbursementModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleDisburse}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Disbursing...' : 'Confirm Disbursement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default LoanReviewPage;
